// src/pos-app/shell/AuroraBackground.js
//
// Aurora Glass primitives (F12a Task 6.1) — the shared visual language every
// POS station screen (Cashier / Waiter / KDS, Phases 7-9) is built from.
// Mirrors the approved mockups (docs/mockups/f12a/*.html): soft brand-coloured
// blobs drifting behind translucent "glass" chrome.
//
//   <AuroraBackground intensity="calm|full">{screen}</AuroraBackground>
//     calm (default) — near-static, very subtle. For working screens that are
//                      stared at all day; the aurora reads as ambience only.
//     full           — richer drift + stronger opacity. For lock / idle /
//                      enrollment moments.
//   Drift is frozen entirely under prefers-reduced-motion.
//
// The glass surface recipe (blur, translucent bg, 1px light border, radius 16)
// is implemented ONCE here and consumed by the other shell components via the
// exported `glassSurface` object / `glass()` helper.
//
// Tokens only: everything reads --c-* theme vars (light + dark both work).
// Deliberate exception pattern: color-mix(in srgb, var(--c-*) N%, transparent)
// for translucency/glows — the tokens stay the single source of hue.
import React from 'react';
import { FONT } from '../../styles/theme';

/* ------------------------------------------------------------------ */
/* Glass surface — the one place the glass recipe lives.               */
/* ------------------------------------------------------------------ */
export const glassSurface = {
    background: 'color-mix(in srgb, var(--c-surface) 72%, transparent)',
    WebkitBackdropFilter: 'blur(18px) saturate(1.5)',
    backdropFilter: 'blur(18px) saturate(1.5)',
    border: '1px solid color-mix(in srgb, var(--c-border) 55%, transparent)',
    borderRadius: 16,
    boxShadow: '0 1px 2px rgba(2,8,23,.06), 0 4px 14px rgba(2,8,23,.1)',
};

export function glass(overrides = {}) {
    return { ...glassSurface, ...overrides };
}

/* ------------------------------------------------------------------ */
/* Liquid Glass — the app-wide material for CHROME (bars, tab bars,    */
/* sheets). One recipe, one place.                                     */
/* ------------------------------------------------------------------ */
//
// `glassSurface` above is the material for CONTENT panels — a flat frosted
// card. Liquid Glass is for the chrome that floats OVER content and has to
// stay legible against whatever scrolls beneath it. Four things make it read
// as glass rather than as "a translucent box":
//
//   1. Blur + saturation lift. Blur alone greys everything behind it; pushing
//      saturation past 1 keeps the colour of the content underneath alive, so
//      the material looks lit rather than fogged.
//   2. A specular rim along the top edge — a 1px gradient highlight. This is
//      what sells thickness; without it the surface reads as flat film.
//   3. A soft ambient shadow ABOVE the bar (negative y), so the chrome appears
//      to sit in front of the content rather than be pasted onto it.
//   4. Contrast safety. Translucency is only acceptable while text stays
//      legible, so the base tint never drops below ~70% opaque and the rules
//      below degrade to a solid surface where backdrop-filter is unsupported.
//
// Applied through the `.aq-liquid` class (defined in the CSS block below,
// injected once by <AuroraBackground/> and therefore available to every
// station) rather than an inline style object, because the specular rim needs
// a pseudo-element.
export const LIQUID_GLASS_CSS = `
.aq-liquid{position:relative;isolation:isolate;
  background:color-mix(in srgb, var(--c-surface) 70%, transparent);
  -webkit-backdrop-filter:blur(28px) saturate(1.8);backdrop-filter:blur(28px) saturate(1.8);}
/* Specular rim — the highlight that gives the material an edge. Sits on top
   of the blur but under the content (z-index:0 inside the isolated stack). */
.aq-liquid::before{content:"";position:absolute;inset-inline:0;top:0;height:1px;z-index:0;
  pointer-events:none;
  background:linear-gradient(90deg,
    transparent 0%,
    color-mix(in srgb, var(--c-white) 70%, transparent) 22%,
    color-mix(in srgb, var(--c-white) 85%, transparent) 50%,
    color-mix(in srgb, var(--c-white) 70%, transparent) 78%,
    transparent 100%);}
:root[data-theme="dark"] .aq-liquid::before{
  background:linear-gradient(90deg,
    transparent 0%,
    color-mix(in srgb, var(--c-white) 16%, transparent) 22%,
    color-mix(in srgb, var(--c-white) 26%, transparent) 50%,
    color-mix(in srgb, var(--c-white) 16%, transparent) 78%,
    transparent 100%);}
.aq-liquid > *{position:relative;z-index:1;}

/* Fallback: without backdrop-filter the tint alone is not legible over
   arbitrary content, so go solid rather than ship unreadable chrome. */
@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))){
  .aq-liquid{background:var(--c-surface);}
}
`;

/* ------------------------------------------------------------------ */
/* Aurora backdrop                                                     */
/* ------------------------------------------------------------------ */
// Blob hues follow the mockups' blue/indigo/cyan/teal quartet, mapped to
// tokens: --c-green (brand blue), --c-purple (indigo), --c-teal (cyan),
// --c-success (teal-green) — same mapping EnrollmentScreen established.
const CSS = `
.aur-root{position:fixed;inset:0;overflow:hidden;background:var(--c-bg);font-family:${FONT};-webkit-font-smoothing:antialiased;}
.aur-layer{position:absolute;inset:0;overflow:hidden;z-index:0;pointer-events:none;}
.aur-blob{position:absolute;border-radius:50%;filter:blur(90px);will-change:transform;}
.aur-b1{width:44vw;height:44vw;inset-inline-start:-10vw;top:-14vw;background:radial-gradient(circle at 35% 35%,var(--c-green) 0%,transparent 70%);}
.aur-b2{width:38vw;height:38vw;inset-inline-end:-8vw;top:-6vw;background:radial-gradient(circle at 60% 40%,var(--c-purple) 0%,transparent 70%);}
.aur-b3{width:40vw;height:40vw;inset-inline-start:24vw;bottom:-18vw;background:radial-gradient(circle at 50% 50%,var(--c-teal) 0%,transparent 70%);}
.aur-b4{width:26vw;height:26vw;inset-inline-end:6vw;bottom:-8vw;background:radial-gradient(circle at 50% 50%,var(--c-success) 0%,transparent 70%);}

/* full — lock / idle / enrollment: visible drift */
.aur-full .aur-blob{opacity:.5;}
.aur-full .aur-b4{opacity:.35;}
:root[data-theme="dark"] .aur-full .aur-blob{opacity:.38;}
:root[data-theme="dark"] .aur-full .aur-b4{opacity:.27;}
.aur-full .aur-b1{animation:aurDrift1 26s ease-in-out infinite alternate;}
.aur-full .aur-b2{animation:aurDrift2 32s ease-in-out infinite alternate;}
.aur-full .aur-b3{animation:aurDrift3 29s ease-in-out infinite alternate;}
.aur-full .aur-b4{animation:aurDrift1 36s ease-in-out infinite alternate-reverse;}

/* calm — working screens: dimmer, near-static (tiny amplitude, glacial pace) */
.aur-calm .aur-blob{opacity:.3;}
.aur-calm .aur-b4{opacity:.2;}
:root[data-theme="dark"] .aur-calm .aur-blob{opacity:.2;}
:root[data-theme="dark"] .aur-calm .aur-b4{opacity:.14;}
.aur-calm .aur-b1{animation:aurCalm1 120s ease-in-out infinite alternate;}
.aur-calm .aur-b2{animation:aurCalm2 140s ease-in-out infinite alternate;}
.aur-calm .aur-b3{animation:aurCalm3 130s ease-in-out infinite alternate;}
.aur-calm .aur-b4{animation:aurCalm1 150s ease-in-out infinite alternate-reverse;}

@keyframes aurDrift1{from{transform:translate(0,0) scale(1);}to{transform:translate(6vw,4vw) scale(1.12);}}
@keyframes aurDrift2{from{transform:translate(0,0) scale(1.05);}to{transform:translate(-5vw,5vw) scale(.95);}}
@keyframes aurDrift3{from{transform:translate(0,0) scale(1);}to{transform:translate(-4vw,-5vw) scale(1.1);}}
@keyframes aurCalm1{from{transform:translate(0,0) scale(1);}to{transform:translate(1.5vw,1vw) scale(1.03);}}
@keyframes aurCalm2{from{transform:translate(0,0) scale(1.02);}to{transform:translate(-1.2vw,1.2vw) scale(.99);}}
@keyframes aurCalm3{from{transform:translate(0,0) scale(1);}to{transform:translate(-1vw,-1.3vw) scale(1.02);}}

@media (prefers-reduced-motion: reduce){
  .aur-blob{animation:none !important;}
}

.aur-content{position:relative;z-index:1;height:100%;display:flex;flex-direction:column;box-sizing:border-box;}
${LIQUID_GLASS_CSS}
`;

export default function AuroraBackground({ intensity = 'calm', children }) {
    const mode = intensity === 'full' ? 'aur-full' : 'aur-calm';

    return (
        <div className={`aur-root ${mode}`}>
            <style>{CSS}</style>
            <div className="aur-layer" aria-hidden="true">
                <div className="aur-blob aur-b1" />
                <div className="aur-blob aur-b2" />
                <div className="aur-blob aur-b3" />
                <div className="aur-blob aur-b4" />
            </div>
            <div className="aur-content">{children}</div>
        </div>
    );
}
